import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryParams } from '@/model';
import { PredictionsConverter } from '@/converters/predictions';
import { PredictionsService } from '../instances/predictions';
import { useCallback, useMemo } from 'react';
import { Predictions, PredictionsStatus } from '@/model/predictions';
import { GetParamsObject } from '@/model/query-params';

export const useGetPredictions = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_PREDICTIONS', params],
    async () => {
      const [err, data] = await PredictionsService.getPredictions(params);
      if (err) {
        throw err;
      }
      return {
        data: PredictionsConverter.toDomainFromPredictionsPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePutPredictionsMutation = () => {
  return useMutation({
    mutationKey: ['PUT_PREDICTIONS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await PredictionsService.putPredictions(id, payload);
      if (err) {
        throw err;
      }
      return PredictionsConverter.toDomainFromPutPredictionsResponse(data);
    },
  });
};

export const useGetPredictionsByUUID = () => {
  return useMutation({
    mutationKey: ['GET_PREDICTIONS_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await PredictionsService.getPredictionsByUUID(uuid);
      if (err) {
        throw err;
      }
      return PredictionsConverter.toDomainFromGetPredictionByUUIDResponse(data);
    },
  });
};

export const useGetPredictionsRanking = () => {
  return useMutation({
    mutationKey: ['GET_PREDICTIONS_RANKING'],
    mutationFn: async () => {
      const [err, data] = await PredictionsService.getPredictionsRanking();
      if (err) {
        throw err;
      }
      return PredictionsConverter.toDomainFromPredictionsRankingResponse(data);
    },
  });
};

export const useGetPredictionsResult = () => {
  return useMutation({
    mutationKey: ['GET_PREDICTIONS_RESULT'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await PredictionsService.getPredictionsResult(uuid);
      if (err) {
        throw err;
      }
      return PredictionsConverter.toDomainFromGetPredictionsResultResponse(data);
    },
  });
};

export const usePostPredictionsMutation = () => {
  return useMutation({
    mutationKey: ['POST_PREDICTIONS'],
    mutationFn: async (payload: any) => {
      const [err, data] = await PredictionsService.postPredictions(payload);
      if (err) {
        throw err;
      }
      return PredictionsConverter.toDomainFromPostPredictionsResponse(data);
    },
  });
};

export const usePutPredictionsInteractiveMutation = () => {
  return useMutation({
    mutationKey: ['PUT_PREDICTIONS_INTERACTIVE'],
    mutationFn: async ({ id, payload }: any) => {
      const [err] = await PredictionsService.putPredictionsInteractive(id, payload);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePutPredictionsResultMutation = () => {
  return useMutation({
    mutationKey: ['PUT_PREDICTIONS_RESULT'],
    mutationFn: async ({ id, payload }: any) => {
      const [err] = await PredictionsService.putPredictionsResult(id, payload);
      if (err) {
        throw err;
      }
    },
  });
};

export const useDeletePredictionsMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_PREDICTIONS'],
    mutationFn: async (id: string) => {
      const [err] = await PredictionsService.deletePredictions(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePredictionIfEnable = (isEnable: boolean) => {
  const query = useQuery(
    ['GET_PREDICTIONS'],
    async (): Promise<Predictions[]> => {
      if (!isEnable) {
        return [];
      }

      let all: Predictions[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const [err, data] = await PredictionsService.getPredictions(
          GetParamsObject(
            {
              status: PredictionsStatus.PUBLISHED,
            },
            undefined,
            900,
          ),
        );
        if (err) throw err;
        all = [...all, ...PredictionsConverter.toDomainFromPredictionsPaginatedResponse(data)];
        totalPages = data.meta.pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return all;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isEnable,
      suspense: !isEnable,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};
